// 通用安全验证服务（2FA / Passkey 重试），与内部参考实现行为对齐
import {
  getVerificationMethodsApi,
  requestSecurityProofApi,
  passkeyVerifyBeginApi,
  passkeyVerifyFinishApi,
} from '@/api/verify'
import type { SecurityProof, VerificationMethodOption } from '@/api/verify'

export interface VerificationMethods {
  methods: VerificationMethodOption[]
  has2FA: boolean
  hasPassword: boolean
  hasPasskey: boolean
  passkeySupported: boolean
}

export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials
  )
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '=='.slice((base64.length + 2) % 4)
  const raw = atob(padded)
  const buffer = new ArrayBuffer(raw.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i)
  return buffer
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let str = ''
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i])
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function prepareCredentialRequestOptions(options: Record<string, unknown>) {
  const prepared: Record<string, unknown> = { ...options }
  if (typeof (options as { challenge?: string }).challenge === 'string') {
    prepared.challenge = base64urlToBuffer((options as { challenge: string }).challenge)
  }
  const allowCredentials = (options as { allowCredentials?: Array<{ id: string; type: string }> })
    .allowCredentials
  if (Array.isArray(allowCredentials)) {
    prepared.allowCredentials = allowCredentials.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    }))
  }
  return prepared as unknown as PublicKeyCredentialRequestOptions
}

function buildAssertionResult(credential: PublicKeyCredential) {
  const resp = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferToBase64url(resp.authenticatorData),
      clientDataJSON: bufferToBase64url(resp.clientDataJSON),
      signature: bufferToBase64url(resp.signature),
      userHandle: resp.userHandle ? bufferToBase64url(resp.userHandle) : null,
    },
  }
}

export class SecureVerificationService {
  /**
   * 某个敏感操作可用的验证方式（new-api v1.0.0：按 scope 下发）
   */
  static async checkAvailableVerificationMethods(scope: string): Promise<VerificationMethods> {
    try {
      const requirements = await getVerificationMethodsApi(scope)
      const methods = requirements?.methods ?? []
      const available = (name: string) => methods.some((m) => m.method === name && m.available)
      return {
        methods,
        has2FA: available('2fa'),
        hasPassword: available('password'),
        hasPasskey: available('passkey'),
        passkeySupported: isPasskeySupported(),
      }
    } catch {
      return {
        methods: [],
        has2FA: false,
        hasPassword: false,
        hasPasskey: false,
        passkeySupported: false,
      }
    }
  }

  /** 2FA 动态码 / 备用码 */
  static async verify2FA(
    scope: string,
    context: Record<string, unknown> | undefined,
    code: string
  ): Promise<SecurityProof> {
    if (!code?.trim()) throw new Error('请输入验证码或备用码')
    return requestSecurityProofApi({ method: '2fa', scope, context, code: code.trim() })
  }

  /** 登录密码（无 2FA 时的兜底方式） */
  static async verifyPassword(
    scope: string,
    context: Record<string, unknown> | undefined,
    password: string
  ): Promise<SecurityProof> {
    if (!password) throw new Error('请输入登录密码')
    return requestSecurityProofApi({ method: 'password', scope, context, password })
  }

  static async verifyPasskey(
    scope: string,
    context: Record<string, unknown> | undefined
  ): Promise<SecurityProof> {
    if (!isPasskeySupported()) throw new Error('当前浏览器不支持 Passkey')
    const begin = (await passkeyVerifyBeginApi({ scope, context })) as {
      options: Record<string, unknown>
    }
    const publicKey = prepareCredentialRequestOptions(begin.options)
    const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
    if (!credential) throw new Error('Passkey 验证被取消')
    return passkeyVerifyFinishApi({
      scope,
      context,
      credential: buildAssertionResult(credential),
    })
  }

  static async verify(params: {
    method: '2fa' | 'password' | 'passkey'
    scope: string
    context?: Record<string, unknown>
    code?: string
    password?: string
  }): Promise<SecurityProof> {
    switch (params.method) {
      case '2fa':
        return this.verify2FA(params.scope, params.context, params.code ?? '')
      case 'password':
        return this.verifyPassword(params.scope, params.context, params.password ?? '')
      case 'passkey':
        return this.verifyPasskey(params.scope, params.context)
      default:
        throw new Error(`不支持的验证方式：${params.method}`)
    }
  }
}
